  import React, { useState } from 'react';
  import pickBy from 'lodash/pickBy';
  import classNames from 'classnames';

  import appSettings from '../../../config/settings';
  import { useConfiguration } from '../../../context/configurationContext';
  import { useRouteConfiguration } from '../../../context/routeConfigurationContext';

  import { FormattedMessage, useIntl } from '../../../util/reactIntl';
  import { isMainSearchTypeKeywords, isOriginInUse } from '../../../util/search';
  import { parse, stringify } from '../../../util/urlHelpers';
  import { createResourceLocatorString, matchPathname, pathByRouteName } from '../../../util/routes';
  import {
    Button,
    LimitedAccessBanner,
    LinkedLogo,
    Modal,
    ModalMissingInformation,
    SearchNavBar,
  } from '../../../components';

  import MenuIcon from './MenuIcon';
  import SearchIcon from './SearchIcon';
  import TopbarSearchForm from './TopbarSearchForm/TopbarSearchForm';
  import TopbarMobileMenu from './TopbarMobileMenu/TopbarMobileMenu';
  import TopbarDesktop from './TopbarDesktop/TopbarDesktop';

  import css from './Topbar.module.css';
  import MetaMapVerificationModal from '../../../components/MetaMapVerification/MetaMapVerificationModal';

  const MAX_MOBILE_SCREEN_WIDTH = 1024;

  const SEARCH_DISPLAY_ALWAYS = 'always';
  const SEARCH_DISPLAY_NOT_LANDING_PAGE = 'notLandingPage';
  const SEARCH_DISPLAY_ONLY_SEARCH_PAGE = 'onlySearchPage';

  const redirectToURLWithModalState = (history, location, modalStateParam) => {
    const { pathname, search, state } = location;
    const searchString = `?${stringify({ [modalStateParam]: 'open', ...parse(search) })}`;
    history.push(`${pathname}${searchString}`, state);
  };

  const redirectToURLWithoutModalState = (history, location, modalStateParam) => {
    const { pathname, search, state } = location;
    const queryParams = pickBy(parse(search), (v, k) => {
      return k !== modalStateParam;
    });
    const stringified = stringify(queryParams);
    const searchString = stringified ? `?${stringified}` : '';
    history.push(`${pathname}${searchString}`, state);
  };

  const isPrimary = o => o.group === 'primary';
  const isSecondary = o => o.group === 'secondary';
  const compareGroups = (a, b) => {
    const isAHigherGroupThanB = isPrimary(a) && isSecondary(b);
    const isALesserGroupThanB = isSecondary(a) && isPrimary(b);
    // Note: sort order is stable in JS
    return isAHigherGroupThanB ? -1 : isALesserGroupThanB ? 1 : 0;
  };
  // Returns links in order where primary links are returned first
  const sortCustomLinks = customLinks => {
    const links = Array.isArray(customLinks) ? customLinks : [];
    return links.sort(compareGroups);
  };

  // Resolves in-app links against route configuration
  const getResolvedCustomLinks = (customLinks, routeConfiguration) => {
    const links = Array.isArray(customLinks) ? customLinks : [];
    return links.map(linkConfig => {
      const { type, href } = linkConfig;
      const isInternalLink = type === 'internal' || href.charAt(0) === '/';
      if (isInternalLink) {
        // Internal link
        try {
          const testURL = new URL('http://my.marketplace.com' + href);
          const matchedRoutes = matchPathname(testURL.pathname, routeConfiguration);
          if (matchedRoutes.length > 0) {
            const found = matchedRoutes[0];
            const to = { search: testURL.search, hash: testURL.hash };
            return {
              ...linkConfig,
              route: {
                name: found.route?.name,
                params: found.params,
                to,
              },
            };
          }
        } catch (e) {
          return linkConfig;
        }
      }
      return linkConfig;
    });
  };

  const isCMSPage = found =>
    found.route?.name === 'CMSPage' ? `CMSPage:${found.params?.pageId}` : null;
  const isInboxPage = found =>
    found.route?.name === 'InboxPage' ? `InboxPage:${found.params?.tab}` : null;
  // Find the name of the current route/pathname.
  // It's used as handle for currentPage check.
  const getResolvedCurrentPage = (location, routeConfiguration) => {
    const matchedRoutes = matchPathname(location.pathname, routeConfiguration);
    if (matchedRoutes.length > 0) {
      const found = matchedRoutes[0];
      const cmsPageName = isCMSPage(found);
      const inboxPageName = isInboxPage(found);
      return cmsPageName ? cmsPageName : inboxPageName ? inboxPageName : `${found.route?.name}`;
    }
  };

  const GenericError = props => {
    const { show } = props;
    const classes = classNames(css.genericError, {
      [css.genericErrorVisible]: show,
    });
    return (
      <div className={classes}>
        <div className={css.genericErrorContent}>
          <p className={css.genericErrorText}>
            <FormattedMessage id="Topbar.genericError" />
          </p>
        </div>
      </div>
    );
  };

  const TopbarComponent = props => {
    const [isVerifyModal, setIsVerifyModal] = useState(false);
    const {
      className,
      rootClassName,
      desktopClassName,
      mobileRootClassName,
      mobileClassName,
      isAuthenticated,
      isLoggedInAs,
      authScopes = [],
      authInProgress,
      currentUser,
      currentUserHasListings,
      currentUserHasOrders,
      currentPage,
      notificationCount = 0,
      intl,
      history,
      location,
      onManageDisableScrolling,
      onResendVerificationEmail,
      sendVerificationEmailInProgress,
      sendVerificationEmailError,
      showGenericError,
      config,
      routeConfiguration,
      showTopbarSearch,
      isSearchPage = false,
    } = props;

    const handleSubmit = values => {
      const { currentSearchParams, history, config, routeConfiguration } = props;

      const topbarSearchParams = () => {
        if (isMainSearchTypeKeywords(config)) {
          return { keywords: values?.keywords };
        }
        // topbar search defaults to 'location' search
        const { search, selectedPlace } = values?.location;
        const { origin, bounds } = selectedPlace;
        const originMaybe = isOriginInUse(config) ? { origin } : {};

        return {
          ...originMaybe,
          address: search,
          bounds,
        };
      };
      const searchParams = {
        ...currentSearchParams,
        ...topbarSearchParams(),
      };
      history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, searchParams));
    };

    const handleLogout = () => {
      const { onLogout, history, routeConfiguration } = props;
      onLogout().then(() => {
        const path = pathByRouteName('LandingPage', routeConfiguration);

        // In production we ensure that data is really lost,
        // but in development mode we use stored values for debugging
        if (appSettings.dev) {
          history.push(path);
        } else if (typeof window !== 'undefined') {
          window.location = path;
        }

        console.log('logged out'); // eslint-disable-line
      });
    };

    const { mobilemenu, mobilesearch, keywords, address, origin, bounds } = parse(location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });

    // Custom links are sorted so that group="primary" are always at the beginning of the list.
    const sortedCustomLinks = sortCustomLinks(config.topbar?.customLinks);
    const customLinks = getResolvedCustomLinks(sortedCustomLinks, routeConfiguration);
    const resolvedCurrentPage = currentPage || getResolvedCurrentPage(location, routeConfiguration);

    const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

    const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
    const isMobileLayout = hasMatchMedia
      ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
      : true;
const isMobileMenuOpen =
  !isVerifyModal && ((isMobileLayout && mobilemenu === 'open') || isSearchPage);
      const isMobileSearchOpen = isMobileLayout && mobilesearch === 'open';

    const mobileMenu = (
      <TopbarMobileMenu
        isAuthenticated={isAuthenticated}
        currentUserHasListings={currentUserHasListings}
        currentUser={currentUser}
        onLogout={handleLogout}
        notificationCount={notificationCount}
        currentPage={resolvedCurrentPage}
        customLinks={customLinks}
        setIsVerifyModal={setIsVerifyModal}
      />
    );

    const topbarSearcInitialValues = () => {
      if (isMainSearchTypeKeywords(config)) {
        return { keywords };
      }

      // Only render current search if full place object is available in the URL params
      const locationFieldsPresent = isOriginInUse(config)
        ? address && origin && bounds
        : address && bounds;
      return {
        location: locationFieldsPresent
          ? {
              search: address,
              selectedPlace: { address, origin, bounds },
            }
          : null,
      };
    };
    const initialSearchFormValues = topbarSearcInitialValues();

    const classes = classNames(rootClassName || css.root, className);

    const { display: searchFormDisplay = SEARCH_DISPLAY_ALWAYS } = config?.topbar?.searchBar || {};

    // Search form is shown conditionally depending on configuration and
    // the current page.
    const showSearchOnAllPages = searchFormDisplay === SEARCH_DISPLAY_ALWAYS;
    const showSearchOnSearchPage =
      searchFormDisplay === SEARCH_DISPLAY_ONLY_SEARCH_PAGE && resolvedCurrentPage === 'SearchPage';
    const showSearchNotOnLandingPage =
      searchFormDisplay === SEARCH_DISPLAY_NOT_LANDING_PAGE && resolvedCurrentPage !== 'LandingPage';

    const showSearchForm =
      showSearchOnAllPages || showSearchOnSearchPage || showSearchNotOnLandingPage;

    const mobileSearchButtonMaybe = showSearchForm ? (
      <Button
        rootClassName={css.searchMenu}
        onClick={() => {}}
        title={intl.formatMessage({ id: 'Topbar.searchIcon' })}
      />
    ) : (
      <div className={css.searchMenu} />
    );

    return (
      <div className={classes}>
        <LimitedAccessBanner
          isAuthenticated={isAuthenticated}
          isLoggedInAs={isLoggedInAs}
          authScopes={authScopes}
          currentUser={currentUser}
          onLogout={handleLogout}
          currentPage={resolvedCurrentPage}
        />
        <div className={classNames(mobileRootClassName || css.container, mobileClassName)}>
          <Button
            rootClassName={css.menu}
            onClick={() => redirectToURLWithModalState(history, location, 'mobilemenu')}
            title={intl.formatMessage({ id: 'Topbar.menuIcon' })}
          >
            <MenuIcon className={css.menuIcon} />
            {/* {notificationDot} */}
          </Button>
          <LinkedLogo
            layout={'mobile'}
            alt={intl.formatMessage({ id: 'Topbar.logoIcon' })}
            linkToExternalSite={config?.topbar?.logoLink}
          />
          {mobileSearchButtonMaybe}
        </div>
        <div className={css.desktop}>
          <TopbarDesktop
            className={desktopClassName}
            currentUserHasListings={currentUserHasListings}
            currentUser={currentUser}
            currentPage={resolvedCurrentPage}
            initialSearchFormValues={initialSearchFormValues}
            intl={intl}
            isAuthenticated={isAuthenticated}
            notificationCount={notificationCount}
            onLogout={handleLogout}
            onSearchSubmit={handleSubmit}
            config={config}
            customLinks={customLinks}
            showSearchForm={showSearchForm}
            showTopbarSearch={showTopbarSearch}
            setIsVerifyModal={setIsVerifyModal}
          />
        </div>
        <Modal
          id="TopbarMobileMenu"
          containerClassName={css.modalContainer}
          isOpen={isMobileMenuOpen}
          onClose={() => redirectToURLWithoutModalState(history, location, 'mobilemenu')}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          {authInProgress ? null : mobileMenu}
        </Modal>
        <Modal
          id="TopbarMobileSearch"
          containerClassName={css.modalContainerSearchForm}
          isOpen={isMobileSearchOpen}
          onClose={() => redirectToURLWithoutModalState(history, location, 'mobilesearch')}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <div className={css.searchContainer}>
            <SearchNavBar />
            <p className={css.mobileHelp}>
              <FormattedMessage id="Topbar.mobileSearchHelp" />
            </p>
          </div>
        </Modal>
        <ModalMissingInformation
          id="MissingInformationReminder"
          containerClassName={css.missingInformationModal}
          currentUser={currentUser}
          currentUserHasListings={currentUserHasListings}
          currentUserHasOrders={currentUserHasOrders}
          location={location}
          onManageDisableScrolling={onManageDisableScrolling}
          onResendVerificationEmail={onResendVerificationEmail}
          sendVerificationEmailInProgress={sendVerificationEmailInProgress}
          sendVerificationEmailError={sendVerificationEmailError}
        />

        {/* {currentUser && currentUser?.id && (
          <MetaMapVerificationModal
            id="IdentityVeficationModal"
            containerClassName={css.missingInformationModal}
            currentUser={currentUser}
            onManageDisableScrolling={onManageDisableScrolling}
=            setIsVerifyModal={setIsVerifyModal}
            isVerifyModal={isVerifyModal}
          />
        )} */}

        <GenericError show={showGenericError} />
      </div>
    );
  };

  /**
   * Topbar containing logo, main search and navigation links.
   *
   * @component
   * @param {Object} props
   * @param {string?} props.className add more style rules in addition to components own css.root
   * @param {string?} props.rootClassName overwrite components own css.root
   * @param {Object} props.desktopClassName add more style rules for TopbarDesktop
   * @param {Object} props.mobileRootClassName overwrite mobile layout root classes
   * @param {Object} props.mobileClassName add more style rules for mobile layout
   * @param {boolean} props.isAuthenticated
   * @param {boolean} props.isLoggedInAs
   * @param {Object} props.currentUser
   * @param {boolean} props.currentUserHasListings
   * @param {boolean} props.currentUserHasOrders
   * @param {string} props.currentPage
   * @param {number} props.notificationCount
   * @param {Function} props.onLogout
   * @param {Function} props.onManageDisableScrolling
   * @param {Function} props.onResendVerificationEmail
   * @param {Object} props.sendVerificationEmailInProgress
   * @param {Object} props.sendVerificationEmailError
   * @param {boolean} props.showGenericError
   * @param {Object} props.history
   * @param {Function} props.history.push
   * @param {Object} props.location
   * @param {string} props.location.search '?foo=bar'
   * @returns {JSX.Element} topbar component
   */
  const Topbar = props => {
    const config = useConfiguration();
    const routeConfiguration = useRouteConfiguration();
    const intl = useIntl();
    return (
      <TopbarComponent
        config={config}
        routeConfiguration={routeConfiguration}
        intl={intl}
        {...props}
      />
    );
  };

  export default Topbar;
